import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import React from 'react';
import { Machine } from '../../services/apiSLTP/materials/machines';
import { Tool } from '../../services/apiSLTP/materials/tools';
import { Truck } from '../../services/apiSLTP/materials/trucks';
import { TFunction } from 'i18next';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import DocumentPdfLayout from './DocumentPdfLayout';

type MaterialPDFProps = {
    logoUrl: string;
    dataMachines: Machine[];
    dataTools: Tool[];
    dataTrucks: Truck[];
    t: TFunction<'translation', undefined>;
};

const styles = StyleSheet.create({
    page: {
        padding: 15,
        gap: 15,
    },
    pageHeader: {
        fontSize: 15,
        textTransform: 'uppercase',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    logoContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000',
        padding: 15,
    },
    logo: {
        height: 25,
        width: 'auto',
    },
    table: {
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableRow: {
        flexDirection: 'row',
        fontSize: 9,
    },
    tableHeader: {
        backgroundColor: '#999',
        fontWeight: 'bold',
        fontSize: 11,
    },
    tableSectionHeader: {
        backgroundColor: '#ccc',
        fontWeight: 'bold',
        fontSize: 10,
    },
    tableCell: {
        padding: 3,
        flex: 1,
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
    },
    tableMainCell: {
        padding: 3,
        flex: 2,
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        fontWeight: 'bold',
    },
    dateText: {
        fontSize: 8,
        color: '#888',
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    logoFooter: {
        height: 15,
        width: 'auto',
    },
});

const MaterialPDF: React.FC<MaterialPDFProps> = ({ logoUrl, dataMachines, dataTools, dataTrucks, t }) => {
    return (
        <DocumentPdfLayout
            fileName={`${new Date().getFullYear()} - ${capitalizeFirstLetter(t('materialList'))}`}
            locale={t('locale')}
            title={capitalizeFirstLetter(t('materialList'))}
        >
            <View style={styles.table}>
                <View style={[styles.tableRow, styles.tableHeader]} fixed>
                    <Text style={[styles.tableMainCell]}>{t('name').toUpperCase()}</Text>
                    <Text style={styles.tableCell}>{t('type').toUpperCase()}</Text>
                    <Text style={styles.tableCell}>{t('model').toUpperCase()}</Text>
                </View>
                {!!dataMachines.length && (
                    <View style={[styles.tableRow, styles.tableSectionHeader]} wrap={false}>
                        <Text style={styles.tableCell}>{t('machines').toUpperCase()}</Text>
                    </View>
                )}
                {dataMachines
                    ?.sort((a, b) => {
                        if ((a.machineTypeId.displayOrder ?? 0) > (b.machineTypeId.displayOrder ?? 0)) return 1;
                        if ((a.machineTypeId.displayOrder ?? 0) < (b.machineTypeId.displayOrder ?? 0)) return -1;
                        return a.designation.localeCompare(b.designation);
                    })
                    .map((item, index) => (
                        <View style={styles.tableRow} key={index} wrap={false}>
                            <Text style={[styles.tableMainCell]}>{item.baseName}</Text>
                            <Text style={styles.tableCell}>{item.machineTypeId.designation}</Text>
                            <Text style={styles.tableCell}>{item.model}</Text>
                        </View>
                    ))}
                {!!dataTools.length && (
                    <View style={[styles.tableRow, styles.tableSectionHeader]} wrap={false}>
                        <Text style={styles.tableCell}>{t('tools').toUpperCase()}</Text>
                    </View>
                )}
                {dataTools
                    ?.sort((a, b) => {
                        if ((a.toolTypeId.displayOrder ?? 0) > (b.toolTypeId.displayOrder ?? 0)) return 1;
                        if ((a.toolTypeId.displayOrder ?? 0) < (b.toolTypeId.displayOrder ?? 0)) return -1;
                        return a.designation.localeCompare(b.designation);
                    })
                    .map((item, index) => (
                        <View style={styles.tableRow} key={index} wrap={false}>
                            <Text style={styles.tableMainCell}>{item.baseName}</Text>
                            <Text style={styles.tableCell}>{item.toolTypeId.designation}</Text>
                            <Text style={styles.tableCell}>{item.model}</Text>
                        </View>
                    ))}
                {!!dataTrucks.length && (
                    <View style={[styles.tableRow, styles.tableSectionHeader]} wrap={false}>
                        <Text style={styles.tableCell}>{t('trucks').toUpperCase()}</Text>
                    </View>
                )}
                {dataTrucks
                    ?.sort((a, b) => {
                        if ((a.truckTypeId.displayOrder ?? 0) > (b.truckTypeId.displayOrder ?? 0)) return 1;
                        if ((a.truckTypeId.displayOrder ?? 0) < (b.truckTypeId.displayOrder ?? 0)) return -1;
                        return a.designation.localeCompare(b.designation);
                    })
                    .map((item, index) => (
                        <View style={styles.tableRow} key={index} wrap={false}>
                            <Text style={styles.tableMainCell}>{item.baseName}</Text>
                            <Text style={styles.tableCell}>{item.truckTypeId.designation}</Text>
                            <Text style={styles.tableCell}>{item.model}</Text>
                        </View>
                    ))}
            </View>
        </DocumentPdfLayout>
    );
};

export default MaterialPDF;
